import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * FeatureAnnouncement — közös feature-bejelentő renderer a React miniappokhoz
 * (F-MINIFEATURE).
 *
 * Presenter komponens: az API-hívásokat NEM ismeri — a features/labels adatot
 * és az onComplete callbacket az app-oldali adapter adja (Codex R2 elv).
 *
 * Támogatott típusok az első körben: modal, video, announcement.
 * (tooltip/hotspot selector-alapú típus miniappban még nincs — YAGNI.)
 *
 * Viselkedés:
 *  - modal/video → sorba kerül, egyszerre pontosan EGY jelenik meg;
 *  - announcement → lebegő buborék jobb alul badge-dzsel, kattintásra modal;
 *  - enabled=false: a sor megmarad, de modal nem nyílik (app-modal ütközés
 *    elkerülése);
 *  - terminális státusz: feature-önként PONTOSAN EGY onComplete hívás —
 *    CTA-nál 'clicked_cta' (a bezárás utána már nem küld 'seen'-t).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './FeatureAnnouncement.scss';
/** YouTube/Vimeo URL → beágyazható embed URL; egyéb → null (natív video tag) */
export function getVideoEmbedUrl(url) {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
    if (yt) {
        return 'https://www.youtube.com/embed/' + yt[1];
    }
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) {
        return 'https://player.vimeo.com/video/' + vimeo[1];
    }
    return null;
}
/** Plain-text tartalom bekezdésekre bontva (nincs HTML-injektálás) */
function renderContent(content) {
    return content
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line, i) => _jsx("p", { children: line }, i));
}
export function FeatureAnnouncement({ features, labels, enabled, onComplete, }) {
    // Terminális állapot: mely kulcsokra ment már ki esemény
    const completedRef = useRef(new Set());
    // A lezárt (eltüntetett) kulcsok — render-szűréshez state-ként is kell
    const [dismissedKeys, setDismissedKeys] = useState(new Set());
    // Az announcement-buborék nyitott állapota
    const [bubbleOpen, setBubbleOpen] = useState(false);
    const complete = useCallback((key, status) => {
        if (completedRef.current.has(key)) {
            return;
        }
        completedRef.current.add(key);
        setDismissedKeys((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        onComplete([key], status);
    }, [onComplete]);
    const pending = useMemo(() => features.filter((f) => !dismissedKeys.has(f.feature_key)), [features, dismissedKeys]);
    const modalQueue = useMemo(() => pending.filter((f) => f.type === 'modal' || f.type === 'video'), [pending]);
    const announcements = useMemo(() => pending.filter((f) => f.type === 'announcement'), [pending]);
    // Az épp látható modal: a queue eleje, VAGY a buborékból megnyitott hír
    const activeModal = enabled && bubbleOpen && announcements.length > 0
        ? announcements[0]
        : enabled && !bubbleOpen && modalQueue.length > 0
            ? modalQueue[0]
            : null;
    const closeActive = useCallback(() => {
        if (!activeModal) {
            return;
        }
        complete(activeModal.feature_key, 'seen');
        if (bubbleOpen && announcements.length <= 1) {
            setBubbleOpen(false);
        }
    }, [activeModal, complete, bubbleOpen, announcements.length]);
    const ctaClick = useCallback(() => {
        if (!activeModal || !activeModal.cta_url) {
            return;
        }
        const key = activeModal.feature_key;
        const url = activeModal.cta_url;
        complete(key, 'clicked_cta');
        // A tracking POST-nak esélyt adunk a navigáció előtt (onComplete indítja) —
        // a szülő adapter fire-and-forget fetch-et használ, rövid késleltetés elég.
        window.setTimeout(() => {
            window.location.href = url;
        }, 150);
    }, [activeModal, complete]);
    // ESC bezárás + cleanup unmountkor
    useEffect(() => {
        if (!activeModal) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                closeActive();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [activeModal, closeActive]);
    if (!features.length) {
        return null;
    }
    const embedUrl = activeModal && activeModal.type === 'video' && activeModal.video_url
        ? getVideoEmbedUrl(activeModal.video_url)
        : null;
    return (_jsxs(_Fragment, { children: [activeModal && (_jsx("div", { className: "featann-overlay", role: "dialog", "aria-modal": "true", "aria-label": activeModal.title, children: _jsxs("div", { className: `featann-modal featann-layout-${activeModal.modal_layout === 'split' ? 'split' : 'stacked'}`, style: activeModal.modal_width ? { maxWidth: activeModal.modal_width + 'px' } : undefined, children: [_jsx("button", { type: "button", className: "featann-close", "aria-label": labels.close, onClick: closeActive, children: _jsx("i", { className: "fa fa-times" }) }), _jsxs("div", { className: "featann-body", children: [_jsxs("div", { className: "featann-text", children: [_jsx("h2", { className: "featann-title", children: activeModal.title }), _jsx("div", { className: "featann-content", children: renderContent(activeModal.content) })] }), activeModal.type === 'video' && activeModal.video_url ? (_jsx("div", { className: "featann-media featann-video", children: embedUrl ? (_jsx("iframe", { src: embedUrl, title: activeModal.title, allow: "accelerometer; encrypted-media; picture-in-picture", referrerPolicy: "no-referrer", allowFullScreen: true })) : (_jsx("video", { src: activeModal.video_url, controls: true })) })) : activeModal.image_url ? (_jsx("div", { className: "featann-media featann-image", children: _jsx("img", { src: activeModal.image_url, alt: "", referrerPolicy: "no-referrer" }) })) : null] }), _jsxs("div", { className: "featann-footer", children: [activeModal.cta_url && activeModal.cta_label ? (_jsx("button", { type: "button", className: "featann-btn featann-btn-cta", onClick: ctaClick, children: activeModal.cta_label })) : null, _jsx("button", { type: "button", className: "featann-btn featann-btn-close", onClick: closeActive, children: labels.close })] })] }) })), announcements.length > 0 && !bubbleOpen && (_jsxs("button", { type: "button", className: "featann-bubble", "aria-label": labels.news_open, title: labels.news_title, onClick: () => setBubbleOpen(true), children: [_jsx("i", { className: "fa fa-bullhorn" }), _jsx("span", { className: "featann-bubble-badge", children: announcements.length })] }))] }));
}
